import { z } from 'zod';
import { jsonByteLength } from './attempt.ts';
import { RichTextDocSchema } from './rich-text.ts';

/**
 * Blog (F7 D-3, D-4, AC-3.1, T-3.1): posts, categories, tags and the cover images in R2.
 *
 * Editors write posts in the admin through `/api/admin/posts` and `/api/admin/categories`; images go
 * up with `POST /api/admin/media` (raw bytes, not JSON) and come back from `GET /api/media/<key>`.
 * Publishing (`published`, `published_at`) is the publish route and scheduler of T-3.4, never the
 * editor's `PUT`; the site reads the published posts at build time (T-3.5).
 *
 * Bodies are strict objects: a field the API does not know is a 422, never stored. The Tiptap
 * document an editor sends is checked against the rich-text allowlist (`RichTextDocSchema`,
 * rich-text.ts, T-3.3); what the API answers is checked for shape and size only (`TiptapDocSchema`),
 * and `@dcs/rich-text/render` drops anything outside the allowlist when it builds the HTML.
 */

/** States of a post. `published` is only ever set by the publish route / scheduler (T-3.4). */
export const POST_STATUSES = ['draft', 'scheduled', 'published'] as const;
/** States an editor may set with `PUT /api/admin/posts/:id`. */
export const POST_EDITABLE_STATUSES = ['draft', 'scheduled'] as const;

export const POST_TITLE_MAX = 200;
export const POST_DESCRIPTION_MAX = 300;
/** Slugs of posts and categories: `a-z`, `0-9` and single hyphens. */
export const BLOG_SLUG_MAX = 80;
/** UTF-8 bytes of the serialised Tiptap document. */
export const POST_BODY_MAX_BYTES = 204_800;
export const POST_TAG_MAX = 40;
export const POST_TAGS_MAX = 10;
export const CATEGORY_NAME_MAX = 80;

/** Image types `POST /api/admin/media` accepts (declared AND sniffed), with their key extension. */
export const MEDIA_TYPES = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/avif': 'avif',
} as const;
export type MediaType = keyof typeof MEDIA_TYPES;
/** Largest image, in bytes (2 MB). Resize before uploading: the Worker never processes images. */
export const MEDIA_MAX_BYTES = 2 * 1024 * 1024;
/** Keys the upload creates and `GET /api/media/*` serves: `blog/<yyyy>/<mm>/<32 hex>.<ext>`. */
export const MEDIA_KEY_PATTERN =
  /^blog\/\d{4}\/(?:0[1-9]|1[0-2])\/[0-9a-f]{32}\.(?:webp|jpg|png|avif)$/;

/**
 * Path of an image on the API: `/api/media/<key>`. The app and admin use it as-is (same origin,
 * through their `/api/*` proxy); the static site prefixes the API origin (`${API_URL}/api/media/...`)
 * since it has no proxy.
 */
export const mediaPath = (key: string) => `/api/media/${key}`;

const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Slug of a title or name: Vietnamese diacritics removed (`đ` → `d`), lower case, every run of other
 * characters → one hyphen, at most `BLOG_SLUG_MAX` characters (cut at a hyphen when one is in the
 * second half). `''` when nothing is left (a title of symbols only). The admin can show the same
 * slug as the API computes.
 */
export function slugify(text: string, max: number = BLOG_SLUG_MAX): string {
  let slug = text
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug.length > max) {
    slug = slug.slice(0, max);
    const cut = slug.lastIndexOf('-');
    if (cut >= max / 2) slug = slug.slice(0, cut);
    slug = slug.replace(/-+$/, '');
  }
  return slug;
}

/** A tag as stored: NFC, trimmed, single spaces, lower case (`" Biển  Báo "` → `"biển báo"`). */
export const normalizeTag = (tag: string) =>
  tag.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

export const PostStatusSchema = z.enum(POST_STATUSES);
export type PostStatus = z.infer<typeof PostStatusSchema>;

export const BlogSlugSchema = z
  .string()
  .max(BLOG_SLUG_MAX)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lower-case letters, digits and single hyphens only');

export const MediaKeySchema = z.string().regex(MEDIA_KEY_PATTERN, 'Not a blog media key');

/** Tiptap node, shape only (T-3.3 checks the node types against its allowlist). */
const TiptapNodeSchema = z.looseObject({ type: z.string().min(1).max(64) });

/**
 * Tiptap JSON document (D-4): `{ type: 'doc', content: [...] }`, at most `POST_BODY_MAX_BYTES` once
 * serialised. The DB has the same byte cap as a CHECK.
 */
export const TiptapDocSchema = z
  .looseObject({
    type: z.literal('doc'),
    content: z.array(TiptapNodeSchema).optional(),
  })
  .superRefine((doc, ctx) => {
    if (jsonByteLength(doc) > POST_BODY_MAX_BYTES) {
      ctx.addIssue({
        code: 'custom',
        message: `At most ${String(POST_BODY_MAX_BYTES)} bytes once serialised`,
      });
    }
  });
export type TiptapDoc = z.infer<typeof TiptapDocSchema>;

/** Tags of a post: normalised (`normalizeTag`), 1-40 characters each, at most 10, duplicates merged. */
export const PostTagsSchema = z
  .array(z.string().transform(normalizeTag).pipe(z.string().min(1).max(POST_TAG_MAX)))
  .max(POST_TAGS_MAX)
  .transform((tags) => [...new Set(tags)]);

const title = z.string().trim().min(1).max(POST_TITLE_MAX);
const description = z.string().trim().max(POST_DESCRIPTION_MAX);
const categoryId = z.string().min(1).max(64);

/**
 * `POST /api/admin/posts`: a new draft. `slug` defaults to `slugify(title)`; a slug in use is a 409
 * `slug_taken` with `suggestion` (the next free `<slug>-<n>`). Status and dates are not accepted
 * here: a new post is always a draft.
 */
export const PostCreateInputSchema = z.strictObject({
  title,
  slug: BlogSlugSchema.optional(),
  description: description.default(''),
  coverKey: MediaKeySchema.nullable().default(null),
  categoryId: categoryId.nullable().default(null),
  tags: PostTagsSchema.default([]),
  bodyJson: RichTextDocSchema.default({ type: 'doc', content: [] }),
});
export type PostCreateInput = z.input<typeof PostCreateInputSchema>;

/**
 * `PUT /api/admin/posts/:id`: the fields to change (absent = unchanged; `tags` replaces the list).
 * `status`: `draft` ↔ `scheduled` only (`scheduled` needs a `publishAt` in the future; `draft`
 * clears it). A published post keeps its status here (409 `post_published`): T-3.4 owns it.
 */
export const PostUpdateInputSchema = z.strictObject({
  title: title.optional(),
  slug: BlogSlugSchema.optional(),
  description: description.optional(),
  coverKey: MediaKeySchema.nullable().optional(),
  categoryId: categoryId.nullable().optional(),
  tags: PostTagsSchema.optional(),
  bodyJson: RichTextDocSchema.optional(),
  status: z
    .enum(POST_EDITABLE_STATUSES, {
      error: "Expected 'draft' or 'scheduled' (publishing is the publish route's)",
    })
    .optional(),
  publishAt: z.iso.datetime({ offset: true }).nullable().optional(),
});
export type PostUpdateInput = z.input<typeof PostUpdateInputSchema>;

const isoDate = z.iso.datetime();

/** A post in the admin list (`GET /api/admin/posts`): no body. */
export const AdminPostSchema = z.strictObject({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  coverKey: z.string().nullable(),
  categoryId: z.string().nullable(),
  status: PostStatusSchema,
  publishAt: isoDate.nullable(),
  publishedAt: isoDate.nullable(),
  authorId: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type AdminPost = z.infer<typeof AdminPostSchema>;

/** One post with its body and tags (`GET /api/admin/posts/:id`, and the write routes' answer). */
export const AdminPostDetailSchema = AdminPostSchema.extend({
  bodyJson: TiptapDocSchema,
  tags: z.array(z.string()),
});
export type AdminPostDetail = z.infer<typeof AdminPostDetailSchema>;

/** `POST /api/admin/categories`; `slug` defaults to `slugify(name)` (409 `slug_taken` if in use). */
export const CategoryCreateInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(CATEGORY_NAME_MAX),
  slug: BlogSlugSchema.optional(),
  sort: z.number().int().min(-1_000_000).max(1_000_000).default(0),
});
export type CategoryCreateInput = z.input<typeof CategoryCreateInputSchema>;

/** `PUT /api/admin/categories/:id`: the fields to change. */
export const CategoryUpdateInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(CATEGORY_NAME_MAX).optional(),
  slug: BlogSlugSchema.optional(),
  sort: z.number().int().min(-1_000_000).max(1_000_000).optional(),
});
export type CategoryUpdateInput = z.input<typeof CategoryUpdateInputSchema>;

export const CategorySchema = z.strictObject({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  sort: z.number().int(),
  createdAt: isoDate,
});
export type Category = z.infer<typeof CategorySchema>;

/** `201` answer of `POST /api/admin/media`. */
export const MediaUploadedSchema = z.strictObject({
  key: MediaKeySchema,
  /** `mediaPath(key)`. */
  url: z.string(),
  contentType: z.enum(Object.keys(MEDIA_TYPES) as [MediaType, ...MediaType[]]),
  size: z.number().int().positive(),
});
export type MediaUploaded = z.infer<typeof MediaUploadedSchema>;

// ---- Public blog (the static site's build, F7 AC-3.5, T-3.5) -----------------------------------------

/** `GET /api/public/posts`: default and largest page (the site build asks for 100 until `nextCursor` is null). */
export const PUBLIC_POSTS_PAGE_DEFAULT = 20;
export const PUBLIC_POSTS_PAGE_MAX = 100;

/** Opaque keyset cursor of the public list (`<published_at ms>.<id>` of the last item of a page). */
export const PublicPostCursorSchema = z
  .string()
  .regex(/^\d{1,15}\.[A-Za-z0-9-]{1,64}$/, 'Invalid cursor');

/** `GET /api/public/posts?limit=&cursor=&category=` (newest first; unknown params are ignored). */
export const PublicPostsQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(PUBLIC_POSTS_PAGE_MAX)
    .default(PUBLIC_POSTS_PAGE_DEFAULT),
  cursor: PublicPostCursorSchema.optional(),
  /** Slug of a category: only its posts. */
  category: BlogSlugSchema.optional(),
});
export type PublicPostsQuery = z.infer<typeof PublicPostsQuerySchema>;

/** A category as the public sees it: no id, no internal order beyond the list's. */
export const PublicCategorySchema = z.strictObject({
  slug: BlogSlugSchema,
  name: z.string().min(1).max(CATEGORY_NAME_MAX),
});
export type PublicCategory = z.infer<typeof PublicCategorySchema>;

/** `GET /api/public/categories`: display order (`sort`, then name), with the published posts of each. */
export const PublicCategoryListSchema = z.strictObject({
  items: z.array(PublicCategorySchema.extend({ postCount: z.number().int().min(0) })),
});
export type PublicCategoryList = z.infer<typeof PublicCategoryListSchema>;

/**
 * A published post without its body. Never the author, the status or the internal ids: the blog is
 * signed by the site, not by the editor's account. `coverUrl` is absolute (`<API origin>/api/media/<key>`,
 * `mediaPath`), since the static site has no `/api` proxy.
 */
export const PublicPostSummarySchema = z.strictObject({
  slug: BlogSlugSchema,
  title: z.string().min(1).max(POST_TITLE_MAX),
  description: z.string().max(POST_DESCRIPTION_MAX),
  coverUrl: z.url().nullable(),
  category: PublicCategorySchema.nullable(),
  tags: z.array(z.string().min(1).max(POST_TAG_MAX)).max(POST_TAGS_MAX),
  publishedAt: isoDate,
  updatedAt: isoDate,
});
export type PublicPostSummary = z.infer<typeof PublicPostSummarySchema>;

/** One page of `GET /api/public/posts`: `total` counts every published post of the filter. */
export const PublicPostListSchema = z.strictObject({
  items: z.array(PublicPostSummarySchema),
  nextCursor: PublicPostCursorSchema.nullable(),
  total: z.number().int().min(0),
});
export type PublicPostList = z.infer<typeof PublicPostListSchema>;

/** `GET /api/public/posts/:slug`: the summary plus the Tiptap document (rendered at build, D-4). */
export const PublicPostSchema = PublicPostSummarySchema.extend({ bodyJson: TiptapDocSchema });
export type PublicPost = z.infer<typeof PublicPostSchema>;
